<template>
  <div class="fixed z-10 inset-0 overflow-y-auto">
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span
      >&#8203;

      <!-- This comment is to trick purgecss into not removing the concatenated classes  -->
      <!-- purgecss:  bg-green-50 bg-yellow-50 bg-red-50 bg-blue-50 -->
      <!-- purgecss:  text-green-400 text-yellow-400 text-red-400 text-blue-400 -->
      <!-- purgecss:  text-green-500 text-yellow-500 text-red-500 text-blue-500 -->
      <!-- purgecss:  text-green-700 text-yellow-700 text-red-700 text-blue-700 -->
      <!-- purgecss:  hover:text-green-100 hover:text-yellow-100 hover:text-red-100 hover:text-blue-100 -->
      <!-- purgecss:  focus:text-green-100 focus:text-yellow-100 focus:text-red-100 focus:text-blue-100 --> 

      <div
        class="inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full p-4"
        :class="`bg-${color}-50`"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <icon
              set="solid"
              :name="icon"
              class="h-5 w-5"
              :class="`text-${color}-400`"
            />
          </div>
          <div class="ml-3">
            <h3
              class="text-sm leading-5 font-medium"
              :class="`text-${color}-800`"
            >
              {{ header }}
            </h3>
            <div class="mt-2 text-sm leading-5" :class="`text-${color}-700`">
              <div v-if="error">
                {{ errorMessage }}

                <div
                  v-if="isValidationError"
                  class="text-sm leading-5 text-red-700"
                >
                  <ul class="list-disc pl-5 space-y-1">
                    <li
                      v-for="(validationMessage, index) in validationErrors"
                      :key="index"
                    >
                      {{ validationMessage }}
                    </li>
                  </ul>
                </div>

                <div
                  v-if="errorUuid"
                  class="flex items-center space-x-2 mt-2 text-sm leading-5 text-red-800"
                >
                  <span>Error ID:</span>
                  <span class="relative">
                    <span
                      v-if="uuidIsCopiedToClipboard"
                      class="absolute inset-0 text-red-500 text-center bg-red-100"
                    >
                      Copied to clipboard!
                    </span>
                    {{ errorUuid }}
                  </span>
                  <button
                    type="button"
                    class="focus:outline-none"
                    v-if="canAccessClipboard"
                    @click="copyToClipboard"
                  >
                    <icon
                      :set="uuidIsCopiedToClipboard ? 'solid' : 'outline'"
                      name="document-duplicate"
                      class="h-5 w-5"
                    />
                  </button>
                </div>
              </div>

              <p v-else>
                {{ message }}
              </p>
            </div>
          </div>
          <div class="ml-auto pl-3">
            <div class="-mx-1.5 -my-1.5">
              <button
                class="inline-flex rounded-md p-1.5 focus:outline-none transition ease-in-out duration-150"
                :class="`text-${color}-500 hover:bg-${color}-100 focus:bg-${color}-100`"
                aria-label="Dismiss"
                @click="$emit('close')"
              >
                <icon
                  set="solid"
                  name="x"
                  class="h-5 w-5"
                  :class="`text-${color}-800`"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Alert",
  props: {
    type: {
      type: String,
      default: "info",
    },
    header: {
      type: String,
      default: "",
    },
    body: {
      type: Object,
      default: () => {},
    },
    error: {
      type: Error,
      default: null,
    },
    message: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      visible: false,
      canAccessClipboard: false,
      uuidIsCopiedToClipboard: false,
    };
  },
  created() {
    navigator.permissions
      .query({ name: "clipboard-write" })
      .then((result) => (this.canAccessClipboard = result.state === "granted"));
  },
  computed: {
    color() {
      switch (this.type.toLowerCase()) {
        case "success":
          return "green";
        case "warning":
          return "yellow";
        case "error":
          return "red";
        default:
          return "blue";
      }
    },
    icon() {
      switch (this.type.toLowerCase()) {
        case "success":
          return "check-circle";
        case "warning":
          return "exclamation";
        case "error":
          return "x-circle";
        default:
          return "information-circle";
      }
    },
    errorUuid() {
      return this.error?.response?.data?.uuid;
    },
    errorMessage() {
      return this.error?.response?.data?.message;
    },
    errorStatus() {
      return this.error?.response?.status;
    },
    errors() {
      return this.error?.response?.data?.errors;
    },
    isValidationError() {
      return this.errorStatus == 422;
    },
    validationErrors() {
      let messages = [];

      for (let field in this.errors) {
        if (!Array.isArray(this.errors[field])) {
          continue;
        }

        messages = messages.concat(this.errors[field]);
      }

      return messages;
    },
  },
  methods: {
    copyToClipboard() {
      navigator.clipboard
        .writeText(this.uuid)
        .then(() => this.highlightClipboard());
    },

    highlightClipboard() {
      this.uuidIsCopiedToClipboard = true;
      setTimeout(() => {
        this.uuidIsCopiedToClipboard = false;
      }, 1000);
    },
  },
};
</script>
